import {
    GetRoleByIdDocument,
} from '@redviking/argonaut-core-ui/types/db';
import { accessor } from '../store';
import { Route } from 'vue-router';
import { RouterViewSlider } from '../components/RouterViewSlider';
import { RouteEntityDetail } from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { messages } from 'src/i18n/i18n';
import { validateRoleSelectAcl, validateRoleUpdateAcl } from './roles.validations';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import {
    entityDetailRoute,
} from '@redviking/argonaut-core-ui/src/components/EntityDetail/entity-route-util';
import { entityPageBannerTitle } from 'src/util/formatting';
import { userIsAdmin } from '@redviking/argonaut-core-ui/src/auth';
import {
    type RoleEntity,
    createRole,
    saveRole,
} from './role.entity';

export default [
    {
        path: '/roles',
        name: 'role-base',
        redirect: { name: 'roles' },
        component: RouterViewSlider,
        meta: {
            navigationGuard: () => validateRoleSelectAcl(),
            navbarTitle: messages.roles.titles.nav,
            navbarIcon: 'mdi-badge-account',
            transition: 'horizontal',
            featureFlags: [],
        },
        children: [
            {
                name: 'roles',
                path: '',
                component: () => import(/* webpackChunkName: "roles" */ './Roles.view.vue'),
                meta: {
                    pageBannerTitle: messages.roles.titles.pageBannerTitle,
                    pageBannerSubtitle: messages.roles.titles.pageBannerSubtitle,
                },
            },
            entityDetailRoute<'role'>({
                path: ':roleId',
                name: 'roleMaintenence',
                component: RouteEntityDetail,
                redirect: to => ({ name: 'roleSettings', params: to.params }),
                meta: {
                    returnRoute: (route: Route) => ({ name: 'roles', query: route.query }),
                    entityType: 'role',
                    canEdit: () => {
                        const role = accessor.entityAsType('role');
                        return (Boolean(role?.contextId) && validateRoleUpdateAcl()) || userIsAdmin();
                    },
                    getEntity: async (to) => {
                        if (to.query.mode === 'create') {
                            const entity: RoleEntity = {
                                acls: {},
                                name: '',
                                enabled: true,
                                id: to.params.roleId,
                                contextId: accessor.auth.contextId,
                            };
                            return {
                                entity,
                            };
                        } else {
                            const { role } = await gqlClient.request({
                                document: GetRoleByIdDocument,
                                variables: {
                                    roleId: to.params.roleId,
                                },
                            });

                            if (!role) {
                                throw new Error('Could not find role');
                            }

                            const entity: RoleEntity = {
                                id: role.id,
                                name: role.name,
                                enabled: role.enabled,
                                contextId: role.context_id || null,
                                acls: role.acls.reduce<Record<string, boolean>>((acls, roleAcl) => {
                                    acls[roleAcl.acl_id] = true;
                                    return acls;
                                }, {}),
                            };

                            return {
                                entity,
                            };
                        }
                    },
                    saveEntity: saveRole,
                    createEntity: createRole,
                    pageBannerTitle: () => entityPageBannerTitle('role', accessor.entityAsType('role')?.name),
                },
                children: [
                    {
                        path: 'settings',
                        name: 'roleSettings',
                        component: () => import(/* webpackChunkName: "roles" */ './RoleSettings.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: messages.settings,
                                icon: 'mdi-cog',
                            },
                        },
                    },
                    {
                        path: 'role-users',
                        name: 'roleUsers',
                        component: () => import(/* webpackChunkName: "roles" */ './RoleUsers.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Users',
                                icon: 'mdi-account-multiple',
                            },
                            navigationGuard: () => validateRoleUpdateAcl(),
                        },
                    },
                ],
            }),
        ],
    },
];
